import {
    Paragraph,
    TextRun,
    Table,
    TableRow,
    TableCell,
    WidthType,
    BorderStyle,
    AlignmentType,
    VerticalAlign,
    HeightRule,
} from "docx";
import { fixColor, fitImage, fetchImageWithDimensions, generateResumePlaceholderImage, FULL_WIDTH, SCALE, createCommonHeader, createCommonFooter } from "@/utils/wordGenerator/common.js";
import { formatNumber } from "@/utils/proposalValidation.js";
import {api_baseURL} from "@/utils/init.js";

// Import Icons
import bookIcon from "@/assets/bookIcon.png";
import arrowCircleIcon from "@/assets/arrowCircleIcon.png";
import globeIcon from "@/assets/globeIcon.png";

export const createResumeTemp4 = async (resumeData, startPage) => {
    const style = resumeData.style || {};
    const sections = [];

    // Fetch Icons
    const bookIconData = await fetchImageWithDimensions(bookIcon);
    const arrowIconData = await fetchImageWithDimensions(arrowCircleIcon);
    const globeIconData = await fetchImageWithDimensions(globeIcon);

    const PAGE_MARGIN = 200;
    const BODY_SPACER = 200;
    const MEMBER_BLOCK_HEIGHT = 7230;
    const CONTENT_WIDTH = FULL_WIDTH - (PAGE_MARGIN * 2);

    let currentAbsPage = startPage || 1;

    // Layout constants
    const sidebarWidthDxa = Math.floor(CONTENT_WIDTH * 0.3);
    const mainWidthDxa = Math.floor(CONTENT_WIDTH * 0.7);
    const spacerWidthDxa = Math.floor(CONTENT_WIDTH * 0.04);
    const projectsWidthDxa = mainWidthDxa - spacerWidthDxa;

    // Helper for Headings with Icons
    const createSidebarHeading = (text, iconData) => {
        const iconRun = iconData ? fitImage(iconData, 280, 280, "contain") : null;
        return new Table({
            width: { size: sidebarWidthDxa, type: WidthType.DXA },
            margins: { top: 50, bottom: 50, left: 50, right: 50 },
            borders: {
                top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.SINGLE, color: "FFFFFF", size: 12 },
                left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
                insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
            },
            rows: [new TableRow({
                children: [
                    new TableCell({
                        width: { size: sidebarWidthDxa, type: WidthType.DXA },
                        verticalAlign: VerticalAlign.CENTER,
                        children: [
                            new Paragraph({
                                alignment: AlignmentType.LEFT,
                                indent: { left: 350, hanging: 350 },
                                spacing: { before: 100 },
                                children: [
                                    ...(iconRun ? [iconRun] : []),
                                    new TextRun({
                                        text: (iconRun ? "\t" : "") + text,
                                        bold: style.heading?.bold || true,
                                        italics: style.heading?.italic,
                                        underline: style.heading?.underline ? {} : undefined,
                                        size: (style.heading?.size || 12) * SCALE,
                                        color: fixColor(style.heading?.color || "#000000"),
                                        font: style.heading?.font
                                    })
                                ]
                            })
                        ]
                    })
                ]
            })]
        });
    };

    // Helper for Education Block (Combined Degree & University)
    const createEducationBlock = (member) => {
        const arrowRun = arrowIconData ? fitImage(arrowIconData, 200, 200, "contain") : null;
        return new Table({
            width: { size: sidebarWidthDxa, type: WidthType.DXA },
            margins: { top: 50, bottom: 50, left: 50, right: 50 },
            borders: {
                top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
                left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
                insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
            },
            rows: [new TableRow({
                children: [
                    new TableCell({
                        width: { size: sidebarWidthDxa, type: WidthType.DXA },
                        verticalAlign: VerticalAlign.TOP,
                        children: [
                            new Paragraph({
                                indent: { left: 350, hanging: 350 },
                                spacing: { before: 100 },
                                children: [
                                    ...(arrowRun ? [arrowRun] : []),
                                    new TextRun({
                                        text: (arrowRun ? "\t" : "") + (member.degree || "N/A"),
                                        bold: style.label?.bold || true,
                                        italics: style.label?.italic,
                                        underline: style.label?.underline ? {} : undefined,
                                        size: (style.label?.size || 9) * SCALE,
                                        color: fixColor(style.label?.color || "#000000"),
                                        font: style.label?.font
                                    })
                                ]
                            }),
                            new Paragraph({
                                spacing: { after: 100 },
                                indent: { left: 350 }, // Align university with degree text
                                children: [new TextRun({
                                    text: member.university || "N/A",
                                    size: (style.text?.size || 8) * SCALE,
                                    color: fixColor(style.text?.color || "#333333"),
                                    font: style.text?.font,
                                    bold: style.text?.bold,
                                    italics: style.text?.italic,
                                    underline: style.text?.underline ? {} : undefined,
                                })]
                            })
                        ]
                    })
                ]
            })]
        });
    };

    // Helper for List Items (Options for Bullets)
    const createListItem = (text, itemStyle, spacingAfter = 0, showBullet = true) => {
        return new Table({
            width: { size: sidebarWidthDxa, type: WidthType.DXA },
            margins: { top: 50, bottom: 50, left: 50, right: 50 },
            borders: {
                top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
                left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
                insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
            },
            rows: [new TableRow({
                children: [
                    new TableCell({
                        width: { size: sidebarWidthDxa, type: WidthType.DXA },
                        verticalAlign: VerticalAlign.TOP,
                        children: [
                            new Paragraph({
                                spacing: { before: 100, after: spacingAfter },
                                indent: showBullet ? { left: 350, hanging: 350 } : undefined,
                                children: [
                                    new TextRun({
                                        text: (showBullet ? "•\t" : "") + text,
                                        ...itemStyle
                                    })
                                ]
                            })
                        ]
                    })
                ]
            })]
        });
    };

    // Group members in pairs (2 per page)
    for (let m = 0; m < resumeData.members.length; m += 2) {
        const memberBlocks = [];
        const projectsPerMember = 3;

        const pageMembers = resumeData.members.slice(m, m + 2);

        for (const member of pageMembers) {
            // ================= SIDEBAR (LEFT) =================
            const sidebarChildren = [];

            // 1. Profile Picture
            let profileImgRun = null;
            const imgUrl = `${api_baseURL}profilePicture?tmid=${member.teamMemberId}`;
            const profileImgData = member.profilePicturePath ?
                await fetchImageWithDimensions(imgUrl) :
                generateResumePlaceholderImage(sidebarWidthDxa, 2000, fixColor(style.colorScheme?.body || "EEEEEE"));

            if (profileImgData) {
                profileImgRun = fitImage(profileImgData, sidebarWidthDxa, 2000, "cover");
            } else {
                profileImgRun = fitImage(generateResumePlaceholderImage(sidebarWidthDxa, 2000, fixColor(style.colorScheme?.body || "EEEEEE")), sidebarWidthDxa, 2000, "cover");
            }

            sidebarChildren.push(new Table({
                width: { size: sidebarWidthDxa, type: WidthType.DXA },
                borders: {
                    top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
                    left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
                    insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
                },
                rows: [new TableRow({
                    height: { value: 2000, rule: HeightRule.EXACT },
                    children: [new TableCell({
                        shading: { fill: fixColor(style.colorScheme?.body || "EEEEEE") },
                        children: [new Paragraph({
                            alignment: AlignmentType.CENTER,
                            children: profileImgRun ? [profileImgRun] : []
                        })]
                    })]
                })]
            }));

            // 2. Name & Role Box
            sidebarChildren.push(new Table({
                width: { size: sidebarWidthDxa, type: WidthType.DXA },
                borders: {
                    top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
                    left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
                    insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
                },
                rows: [new TableRow({
                    children: [new TableCell({
                        shading: { fill: "E66E2D" },
                        margins: { top: 50, bottom: 50, left: 50, right: 50 },
                        children: [
                            new Paragraph({
                                alignment: AlignmentType.CENTER,
                                children: [
                                    new TextRun({
                                        text: (member.teamMemberName || "N/A").toUpperCase(),
                                        color: "FFFFFF",
                                        bold: true,
                                        size: 14 * SCALE,
                                        font: "sans-serif"
                                    }),
                                    new TextRun({ break: 1 }),
                                    new TextRun({
                                        text: (member.role || "N/A").toUpperCase(),
                                        color: "FFFFFF",
                                        size: 14 * SCALE,
                                        font: "sans-serif"
                                    })
                                ]
                            })
                        ]
                    })]
                })]
            }));

            // 3. Education
            sidebarChildren.push(createSidebarHeading("EDUCATION", bookIconData));
            if (member.education && member.education.length > 0) {
                member.education.forEach(edu => {
                    sidebarChildren.push(createEducationBlock(edu));
                });
            } else {
                sidebarChildren.push(createEducationBlock({ degree: "N/A", university: "N/A" }));
            }

            // 4. Affiliations & Certificates
            sidebarChildren.push(createSidebarHeading("AFFILIATIONS & CERTIFICATES", globeIconData));
            if (member.certifications && member.certifications.length > 0) {
                member.certifications.forEach((cert, index) => {
                    const isLast = index === member.certifications.length - 1;
                    sidebarChildren.push(createListItem(cert, {
                        size: (style.text?.size || 8) * SCALE,
                        color: fixColor(style.text?.color || "#333333"),
                        font: style.text?.font,
                        bold: style.text?.bold,
                        italics: style.text?.italic,
                        underline: style.text?.underline ? {} : undefined,
                    }, isLast ? 100 : 0, true));
                });
            } else {
                sidebarChildren.push(createListItem("N/A", {
                    size: (style.text?.size || 8) * SCALE,
                    color: fixColor(style.text?.color || "#333333"),
                    font: style.text?.font
                }, 100, true));
            }

            const sidebarTopContent = [...sidebarChildren];
            sidebarChildren.length = 0;

            // 5. Experience Box
            const experienceBox = new Table({
                width: { size: sidebarWidthDxa, type: WidthType.DXA },
                borders: {
                    top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
                    left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
                    insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
                },
                rows: [new TableRow({
                    children: [new TableCell({
                        shading: { fill: "373236" },
                        margins: { top: 50, bottom: 50 },
                        children: [
                            new Paragraph({
                                alignment: AlignmentType.CENTER,
                                children: [new TextRun({
                                    text: `${member.yearsOfExperience || 0} YEARS EXPERIENCE`,
                                    color: "FFFFFF",
                                    bold: true,
                                    size: 10 * SCALE,
                                    font: "sans-serif"
                                })]
                            })
                        ]
                    })]
                })]
            });

            const memberSidebarTable = new Table({
                width: { size: sidebarWidthDxa, type: WidthType.DXA },
                height: { value: MEMBER_BLOCK_HEIGHT, rule: HeightRule.EXACT },
                borders: {
                    top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
                    left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
                    insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
                },
                rows: [
                    new TableRow({
                        height: { value: Math.floor(MEMBER_BLOCK_HEIGHT * 0.9), rule: HeightRule.EXACT },
                        children: [new TableCell({
                            verticalAlign: VerticalAlign.TOP,
                            children: sidebarTopContent
                        })]
                    }),
                    new TableRow({
                        height: { value: Math.floor(MEMBER_BLOCK_HEIGHT * 0.1), rule: HeightRule.EXACT },
                        children: [new TableCell({
                            verticalAlign: VerticalAlign.BOTTOM,
                            children: [experienceBox]
                        })]
                    })
                ]
            });

            // ================= MAIN CONTENT (RIGHT) =================
            const projectsAreaHeight = MEMBER_BLOCK_HEIGHT - 200;
            const projHeight = Math.floor(projectsAreaHeight / projectsPerMember);
            const experienceChildren = [];
            const pageProjects = (member.projects || []).slice(0, projectsPerMember);

            for (const proj of pageProjects) {
                let projImgRun = null;
                const targetPhoto = proj.uploadedPhoto || proj.photoURL;
                if (targetPhoto) {
                    const imgData = await fetchImageWithDimensions(targetPhoto);
                    if (imgData) {
                        projImgRun = fitImage(imgData, 2000, 1400, "cover");
                    }
                }

                experienceChildren.push(new Table({
                    margins: { top: 100, bottom: 100 },
                    width: { size: projectsWidthDxa, type: WidthType.DXA },
                    borders: {
                        top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
                        left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
                        insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
                    },
                    rows: [new TableRow({
                        height: { value: projHeight - 40, rule: HeightRule.EXACT },
                        children: [
                            new TableCell({
                                width: { size: 2000, type: WidthType.DXA },
                                children: [new Table({
                                    width: { size: 2000, type: WidthType.DXA },
                                    borders: {
                                        top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
                                        left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
                                        insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
                                    },
                                    rows: [new TableRow({
                                        height: { value: 1400, rule: HeightRule.EXACT },
                                        children: [new TableCell({
                                            shading: projImgRun ? undefined : { fill: "FFFFFF" },
                                            children: [new Paragraph({
                                                alignment: AlignmentType.CENTER,
                                                children: projImgRun ? [projImgRun] : []
                                            })]
                                        })]
                                    })]
                                })]
                            }),
                            new TableCell({ width: { size: 200, type: WidthType.DXA }, children: [] }),
                            new TableCell({
                                width: { size: projectsWidthDxa - 2200, type: WidthType.DXA },
                                children: [
                                    new Paragraph({
                                        spacing: { after: 10 },
                                        children: [
                                            new TextRun({
                                                text: (proj.projectName || "N/A") + " ",
                                                bold: style.label?.bold || true,
                                                italics: style.label?.italic,
                                                underline: style.label?.underline ? {} : undefined,
                                                size: (style.label?.size || 10) * SCALE,
                                                color: fixColor(style.label?.color || "#000000"),
                                                font: style.label?.font
                                            }),
                                            new TextRun({
                                                text: `Size: ${proj.projectSize ? formatNumber(proj.projectSize) + " SF" : "N/A"} | `,
                                                size: (style.text?.size || 8) * SCALE,
                                                color: fixColor(style.text?.color || "#333333"),
                                                font: style.text?.font,
                                                bold: style.text?.bold,
                                                italics: style.text?.italic,
                                                underline: style.text?.underline ? {} : undefined,
                                            }),
                                            new TextRun({
                                                text: `Value: ${proj.value ? "$" + formatNumber(proj.value) : "N/A"} | `,
                                                size: (style.text?.size || 8) * SCALE,
                                                color: fixColor(style.text?.color || "#333333"),
                                                font: style.text?.font,
                                                bold: style.text?.bold,
                                                italics: style.text?.italic,
                                                underline: style.text?.underline ? {} : undefined,
                                            }),
                                            new TextRun({
                                                text: `Owner: ${proj.client || "N/A"} | `,
                                                size: (style.text?.size || 8) * SCALE,
                                                color: fixColor(style.text?.color || "#333333"),
                                                font: style.text?.font,
                                                bold: style.text?.bold,
                                                italics: style.text?.italic,
                                                underline: style.text?.underline ? {} : undefined,
                                            }),
                                            new TextRun({
                                                text: `Completion Date: ${proj.constructionCompletionDate ? new Date(proj.constructionCompletionDate).getFullYear() : "N/A"} | `,
                                                size: (style.text?.size || 8) * SCALE,
                                                color: fixColor(style.text?.color || "#333333"),
                                                font: style.text?.font,
                                                bold: style.text?.bold,
                                                italics: style.text?.italic,
                                                underline: style.text?.underline ? {} : undefined,
                                            }),
                                            new TextRun({
                                                text: `Scope: ${proj.projectDetails || "N/A"}`,
                                                size: (style.text?.size || 8) * SCALE,
                                                color: fixColor(style.text?.color || "#333333"),
                                                font: style.text?.font,
                                                bold: style.text?.bold,
                                                italics: style.text?.italic,
                                                underline: style.text?.underline ? {} : undefined,
                                            })
                                        ]
                                    })
                                ]
                            })
                        ]
                    })]
                }));
                experienceChildren.push(new Paragraph({ spacing: { after: 20 } }));
            }

            // ================= ASSEMBLE MEMBER BLOCK =================
            const memberBlockTable = new Table({
                width: { size: CONTENT_WIDTH, type: WidthType.DXA },
                borders: {
                    top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
                    left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
                    insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
                },
                rows: [new TableRow({
                    height: { value: MEMBER_BLOCK_HEIGHT, rule: HeightRule.EXACT },
                    children: [
                        new TableCell({
                            width: { size: sidebarWidthDxa, type: WidthType.DXA },
                            shading: { fill: fixColor(style.colorScheme?.body || "EEEEEE") },
                            children: [memberSidebarTable]
                        }),
                        new TableCell({ width: { size: spacerWidthDxa, type: WidthType.DXA }, children: [] }),
                        new TableCell({
                            margins: { left: 100, right: 100 },
                            width: { size: projectsWidthDxa, type: WidthType.DXA },
                            shading: { fill: fixColor(style.colorScheme?.body || "EEEEEE") },
                            children: experienceChildren
                        })
                    ]
                })]
            });

            memberBlocks.push(memberBlockTable);
        }

        // ================= ASSEMBLE PAGE CONTENT =================
        const headerTable = createCommonHeader(resumeData.title, fixColor(style.colorScheme?.header || "#545454"));
        const footerTable = createCommonFooter(currentAbsPage, fixColor(style.colorScheme?.footer || "#545454"));

        const pageTable = new Table({
            width: { size: FULL_WIDTH, type: WidthType.DXA },
            borders: {
                top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
                left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
                insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
            },
            rows: [
                new TableRow({ children: [new TableCell({ children: [headerTable] })] }),
                new TableRow({ height: { value: BODY_SPACER, rule: HeightRule.EXACT }, children: [new TableCell({ children: [] })] }),
                new TableRow({
                    children: [new TableCell({
                        children: [new Table({
                            width: { size: FULL_WIDTH, type: WidthType.DXA },
                            borders: {
                                top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
                                left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
                                insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
                            },
                            rows: [new TableRow({
                                children: [
                                    new TableCell({ width: { size: PAGE_MARGIN, type: WidthType.DXA }, children: [] }),
                                    new TableCell({
                                        width: { size: CONTENT_WIDTH, type: WidthType.DXA },
                                        children: [
                                            memberBlocks[0] || new Paragraph(""),
                                            new Paragraph({ spacing: { before: 100, } }),
                                            memberBlocks[1] || new Paragraph("")
                                        ]
                                    }),
                                    new TableCell({ width: { size: PAGE_MARGIN, type: WidthType.DXA }, children: [] }),
                                ]
                            })]
                        })]
                    })]
                }),
                new TableRow({ height: { value: BODY_SPACER, rule: HeightRule.AT_LEAST }, children: [new TableCell({ children: [] })] }),
                new TableRow({ children: [new TableCell({ children: [footerTable] })] })
            ]
        });

        sections.push({
            properties: { page: { margin: { top: 0, right: 0, bottom: 0, left: 0 } } },
            children: [pageTable]
        });
        currentAbsPage++;
    }

    return sections;
};
